import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import Swal from "sweetalert2";
import { AuthContext } from "../../../context/authcontext";
import "bootstrap/dist/js/bootstrap.bundle";
import { Modal } from "bootstrap";
import { API_BASE_URL } from "../../../environment";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
const route = all_routes;

const Security = () => {
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);

  const navigate = useNavigate();
  const userId = auth?.user?.id || auth?.user_id || auth?.id;
  const [tenantUserId, setTenantUserId] = useState<number | null>(null);
  const authHeader = localStorage.getItem("authCred") || "";
  const [createdDatetime, setCreatedDatetime] = useState<string>("");
  const [updatedDatetime, setUpdatedDatetime] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    if (!userId || !selectedTenantId || !authHeader) return;
    const fetchTenantUserId = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}&user_id=${userId}`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.results && data.results.length > 0) {
            setTenantUserId(data.results[0].tenant_user_id);
          }
        }
      } catch {}
    };
    fetchTenantUserId();

    setIsLoading(true);

    fetch(`${API_BASE_URL}/user/${userId}/`, {
      headers: {
        Authorization: `Basic ${authHeader}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch user info");
        return res.json();
      })
      .then((data) => {
        setCreatedDatetime(data.created_datetime);
        setUpdatedDatetime(data.updated_datetime);
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId, selectedTenantId, authHeader]);
  function formatDateTime(iso: string) {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const strengthRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!strengthRegex.test(newPassword)) {
      Swal.fire({
        icon: "error",
        title: "Weak password",
        text: "Your new password must be at least 8 characters long and include at least one uppercase letter, one number, and one special character.",
        confirmButtonText: "OK",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Passwords do not match",
        text: "Please make sure your new password and confirm password match.",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/${userId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change password");
      }

      const modalEl = document.getElementById("change_password");
      if (modalEl) {
        const bsModal = Modal.getOrCreateInstance(modalEl);

        modalEl.addEventListener(
          "hidden.bs.modal",
          () => {
            bsModal.dispose();

            modalEl.classList.remove("fade", "show", "modal");
            modalEl.removeAttribute("role");
            modalEl.removeAttribute("aria-modal");
            modalEl.setAttribute("aria-hidden", "true");

            document
              .querySelectorAll(".modal-backdrop")
              .forEach((b) => b.remove());
            document.body.classList.remove("modal-open");
          },
          { once: true }
        );

        bsModal.hide();
      }

      await Swal.fire({
        icon: "success",
        title: "Password Changed",
        text: "Your password has been updated. Please login again.",
        confirmButtonText: "OK",
      });

      setAuth(null);
      localStorage.removeItem("authResponse");
      localStorage.removeItem("authTimestamp");
      localStorage.removeItem("authCred");
      navigate(route.login);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeleteAccount = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tenant-user/${tenantUserId}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Basic ${authHeader}` },
        }
      );
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to delete account");
      }

      const modalEl = document.getElementById("delete_account");
      if (modalEl) {
        const bsModal = Modal.getOrCreateInstance(modalEl);
        modalEl.addEventListener(
          "hidden.bs.modal",
          async () => {
            bsModal.dispose();
            modalEl.classList.remove("fade", "show", "modal");
            document
              .querySelectorAll(".modal-backdrop")
              .forEach((b) => b.remove());
            document.body.classList.remove("modal-open");

            await Swal.fire({
              icon: "success",
              title: "Account Deleted",
              text: "See you again soon! ",
              timer: 3500,
              timerProgressBar: true,
              showConfirmButton: false,
            });

            setAuth(null);
            localStorage.removeItem("authResponse");
            localStorage.removeItem("authTimestamp");
            localStorage.removeItem("authCred");
            navigate(route.login);
          },
          { once: true }
        );
        bsModal.hide();
      } else {
        await Swal.fire({
          icon: "success",
          title: "Deleted",
          timer: 2000,
          showConfirmButton: false,
        });
        setAuth(null);
        navigate(route.login);
      }
    } catch (error: any) {
      Swal.fire({ icon: "error", title: "Oops…", text: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeactivateAccount = async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tenant-user/${tenantUserId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
        body: JSON.stringify({ is_active: false }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to deactivate account");
      }

      const modalEl = document.getElementById("deactive_account");
      if (modalEl) {
        const bsModal = Modal.getOrCreateInstance(modalEl);
        await new Promise<void>((resolve) => {
          modalEl.addEventListener(
            "hidden.bs.modal",
            () => {
              bsModal.dispose();
              modalEl.classList.remove("fade", "show", "modal");
              document
                .querySelectorAll(".modal-backdrop")
                .forEach((b) => b.remove());
              document.body.classList.remove("modal-open");
              resolve();
            },
            { once: true }
          );
          bsModal.hide();
        });
      }

      await Swal.fire({
        icon: "success",
        title: "Account Deactivated",
        text: "Your account has been deactivated.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      setAuth(null);
      localStorage.removeItem("authResponse");
      localStorage.removeItem("authTimestamp");
      localStorage.removeItem("authCred");
      navigate(route.login);
    } catch (e: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: e.message,
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="general"
                    variant="bottom"
                    includeIcons={true}
                    context="profile"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  {/* Settings Sidebar */}
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link to={route.profile} className="fw-medium">
                            Profile
                          </Link>
                          <Link
                            to={route.security}
                            className="fw-medium active"
                          >
                            Security
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Settings Sidebar */}
                </div>
                <div className="col-xl-9 col-lg-12">
                  {/* Settings Info */}
                  <div className="card">
                    <div className="card-body pb-0">
                      <h4 className="fw-semibold mb-3">Security Settings</h4>
                      <div className="row">
                        <div className="col-lg-4 col-md-6 d-flex">
                          <div className="card border shadow-none flex-fill mb-3">
                            <div className="card-body d-flex justify-content-between flex-column">
                              <div className="mb-3">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <h6 className="fw-semibold">Password</h6>
                                </div>
                                {/* <p>Last Changed {formatDateTime(updatedDatetime)}</p> */}
                                Update your password to keep your account
                                secure.
                              </div>
                              <div>
                                <Link
                                  to="#"
                                  className="btn btn-primary"
                                  data-bs-toggle="modal"
                                  data-bs-target="#change_password"
                                >
                                  Change Password
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6 d-flex">
                          <div className="card border shadow-none flex-fill mb-3">
                            <div className="card-body d-flex justify-content-between flex-column">
                              <div className="mb-3">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <h6 className="fw-semibold">
                                    Deactive Account
                                  </h6>
                                </div>
                                {/* <p>Last Changed {formatDateTime(updatedDatetime)}</p> */}
                                Temporarily disable your account and all
                                associated access.
                              </div>
                              <div className="d-flex align-items-center">
                                <Link
                                  to="#"
                                  className="btn btn-warning"
                                  data-bs-toggle="modal"
                                  data-bs-target="#deactive_account"
                                >
                                  Deactive
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6 d-flex">
                          <div className="card border shadow-none flex-fill mb-3">
                            <div className="card-body d-flex justify-content-between flex-column">
                              <div className="mb-3">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <h6 className="fw-semibold">
                                    Delete Account
                                  </h6>
                                </div>
                                <p>
                                  Created on{" "}
                                  {formatCustomDateTime(createdDatetime)}
                                </p>
                              </div>
                              <div className="d-flex align-items-center">
                                <Link
                                  to="#"
                                  className="btn btn-danger"
                                  data-bs-toggle="modal"
                                  data-bs-target="#delete_account"
                                >
                                  Delete Account
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Settings Info */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <>
        {/* /Change Password */}
        <div
          className="modal fade"
          id="change_password"
          role="dialog"
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Password</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              {/* inside your Change Password modal */}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="col-form-label">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                      <span
                        className="input-group-text"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? (
                          <i className="ti ti-eye"></i>
                        ) : (
                          <i className="ti ti-eye-off"></i>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="mb-0">
                    <label className="col-form-label">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                      <span
                        className="input-group-text"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? (
                          <i className="ti ti-eye"></i>
                        ) : (
                          <i className="ti ti-eye-off"></i>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* Delete Account */}
        <div
          className="modal custom-modal fade"
          id="delete_account"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <form>
                  <div className="text-center">
                    <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                      <i className="ti ti-trash-x fs-36 text-danger" />
                    </div>
                    <h4 className="mb-2">Delete Account</h4>
                    <p className="mb-0">Are you sure you want to remove it.</p>
                    <div className="d-flex align-items-center justify-content-center mt-4">
                      <Link
                        to="#"
                        className="btn btn-light me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </Link>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDeleteAccount}
                        disabled={isSubmitting}
                      >
                        Yes, Delete it
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Account */}
        {/* Delete Account */}
        <div
          className="modal custom-modal fade"
          id="delete_two_factor"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Delete Two Factor</h4>
                  <p className="mb-0">Are you sure you want to remove it.</p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button
                      type="button"
                      data-bs-dismiss="modal"
                      className="btn btn-danger"
                    >
                      Yes, Delete it
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Deactive Account */}
        {/* Deactivate Account */}
        <div
          className="modal custom-modal fade"
          id="deactive_account"
          role="dialog"
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center">
                <div className="avatar avatar-xl bg-warning-light rounded-circle mb-3">
                  <i className="ti ti-exclamation fs-36 text-warning" />
                </div>
                <h4 className="mb-2">Deactivate Account</h4>
                <p className="mb-0">
                  Are you sure you want to deactivate your account?
                </p>
                <div className="d-flex justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleDeactivateAccount}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Deactivating…" : "Yes, Deactivate"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default Security;
